"use client";
declare var google: any;
import React, { useEffect, useRef, useState } from "react";
import { Input } from "@fm/ui";
import { loadGoogleMapsAPI } from "../utils/googleMapsLoader";

interface StructuredAddress {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  lat: number;
  lng: number;
}

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (place: StructuredAddress) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

const CustomerAddressAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Enter address...",
  className,
  style,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any | null>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const scrollPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Load Google Maps API using shared loader
  useEffect(() => {
    // Note: loadGoogleMapsAPI in web-components might expect an API key argument
    // dependent on implementation, but checking GooglePlacesAutocomplete usage:
    // It passes apiKey. The 'CustomerAddressAutocomplete' from apps/web didn't pass one?
    // Let's check the apps/web version again...
    // Wait, apps/web version imported from @/utils/googleMapsLoader which might be different.
    // In packages/web-components/src/utils/googleMapsLoader.ts, let's assume it matches GooglePlacesAutocomplete usage.
    // Actually, looking at GooglePlacesAutocomplete.tsx provided earlier:
    // loadGoogleMapsAPI(apiKey).then(...)
    // So I DO need to pass apiKey here if I want it to work in this package.
    // BUT the apps/web version didn't take apiKey as a prop.
    // I should probably follow the pattern of GooglePlacesAutocomplete in this package and accept apiKey.
    // However, the user wants "the same component".
    // If I add apiKey prop, it's safer. functionality is what matters.
    // Let's stick to the apps/web logic BUT add apiKey support because this package seems to require it.
    // Actually, let's verify loadGoogleMapsAPI signature in the next step if needed, but for now safe to add it.
    // Wait, the apps/web version line 46: loadGoogleMapsAPI().then...
    // The web-components version line 46: loadGoogleMapsAPI(apiKey).then...
    // This is a discrepancy. I will generate this file assuming I should pass apiKey if possible,
    // OR I can use the environment variable directly if the loader supports it.
    // Let's look at `BookingSummary` usage: I was passing apiKey to `GooglePlacesAutocomplete`.
    // I should add apiKey prop to this new component to make it compatible with the loader in this package.

    // Changing standard: accepting apiKey to match package loader signature.
    loadGoogleMapsAPI(process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "")
      .then(() => {
        setIsGoogleLoaded(true);
      })
      .catch((error) => {
        console.error("Error loading Google Maps API:", error);
      });
  }, []);

  // Initialize autocomplete
  useEffect(() => {
    if (!isGoogleLoaded || !inputRef.current) return;

    // Create autocomplete with US-only restrictions
    autocompleteRef.current = new google.maps.places.Autocomplete(
      inputRef.current,
      {
        types: ["address"],
        componentRestrictions: { country: ["us"] },
        strictBounds: true,
        bounds: new google.maps.LatLngBounds(
          new google.maps.LatLng(24.396308, -125.0), // Southwest corner
          new google.maps.LatLng(49.384358, -66.93457), // Northeast corner
        ),
        fields: ["address_components", "formatted_address", "geometry"],
      },
    );

    // Try to prevent the autocomplete from appending to body if we're in a modal
    const pacContainer = document.querySelector(".pac-container");
    if (pacContainer) {
      // Find the modal container and append the dropdown there
      const modalContent = inputRef.current?.closest(
        '[data-slot="dialog-content"]',
      );
      if (modalContent) {
        modalContent.appendChild(pacContainer);
      }
    }

    // Fix z-index and pointer events for dropdown to appear above modals and be clickable
    const addHighZIndex = () => {
      const pacContainer = document.querySelector(
        ".pac-container",
      ) as HTMLElement;
      if (pacContainer) {
        pacContainer.style.zIndex = "9999";
        pacContainer.style.pointerEvents = "auto";

        // Ensure the dropdown items are clickable
        const pacItems = pacContainer.querySelectorAll(".pac-item");
        pacItems.forEach((item) => {
          const itemElement = item as HTMLElement;
          itemElement.style.pointerEvents = "auto";
          itemElement.style.cursor = "pointer";
        });
      }
    };

    // Add z-index and pointer events fix when dropdown appears
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (element.classList?.contains("pac-container")) {
                const pacContainer = element as HTMLElement;
                pacContainer.style.zIndex = "9999";
                pacContainer.style.pointerEvents = "auto";

                // Also fix pointer events for all items
                setTimeout(() => {
                  const pacItems = pacContainer.querySelectorAll(".pac-item");
                  pacItems.forEach((item) => {
                    const itemElement = item as HTMLElement;
                    itemElement.style.pointerEvents = "auto";
                    itemElement.style.cursor = "pointer";
                  });
                }, 10);
              }
            }
          });
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also set initial z-index and pointer events
    setTimeout(addHighZIndex, 100);
    setTimeout(addHighZIndex, 500); // Additional attempt after a longer delay

    let isProcessing = false;
    let processingTimeout: NodeJS.Timeout | null = null;

    const tryGeocoderFallback = (address: string) => {
      if (!window.google?.maps?.Geocoder) {
        return;
      }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        {
          address: address,
          componentRestrictions: { country: "US" },
        },
        (results: any, status: any) => {
          if (status === "OK" && results && results[0]) {
            const result = results[0];

            // Convert geocoder result to place format and process
            const placeResult: any = {
              formatted_address: result.formatted_address,
              geometry: result.geometry,
              address_components: result.address_components,
            };

            processPlaceData(placeResult);
          } else {
            console.error("❌ Geocoding failed:", status);
          }
        },
      );
    };

    const processPlaceData = (place: any) => {
      if (isProcessing) {
        return;
      }

      isProcessing = true;

      // Clear any existing timeout
      if (processingTimeout) {
        clearTimeout(processingTimeout);
      }

      // Save current scroll position to prevent unwanted scrolling
      scrollPositionRef.current = {
        x: window.scrollX,
        y: window.scrollY,
      };

      // Verify it's a US address
      const countryComponent = place.address_components?.find(
        (component: any) => component.types.includes("country"),
      );

      if (countryComponent?.short_name === "US") {
        // Extract address components more robustly
        const addressComponents = place.address_components || [];

        const componentMap = new Map<string, any>();
        addressComponents.forEach((c: any) => {
          c.types.forEach((t: any) => {
            if (!componentMap.has(t)) componentMap.set(t, c);
          });
        });

        const streetNumber = componentMap.get("street_number")?.long_name ?? "";
        const route = componentMap.get("route")?.long_name ?? "";
        const subpremise = componentMap.get("subpremise")?.long_name ?? "";

        const city =
          componentMap.get("locality")?.long_name ??
          componentMap.get("sublocality")?.long_name ??
          componentMap.get("sublocality_level_1")?.long_name ??
          componentMap.get("administrative_area_level_3")?.long_name ??
          componentMap.get("administrative_area_level_2")?.long_name ??
          "";

        const state =
          componentMap.get("administrative_area_level_1")?.short_name ?? "";

        const postal = componentMap.get("postal_code")?.long_name ?? "";
        const postalSuffix =
          componentMap.get("postal_code_suffix")?.long_name ?? "";
        const zipCode = postalSuffix ? `${postal}-${postalSuffix}` : postal;

        const formatted = place.formatted_address || "";
        const primaryAddress = [streetNumber, route].filter(Boolean).join(" ");
        const withUnit = subpremise
          ? `${primaryAddress} Apt ${subpremise}`
          : primaryAddress;
        const displayAddress = formatted || withUnit || primaryAddress;

        const lat = place.geometry?.location?.lat() ?? 0;
        const lng = place.geometry?.location?.lng() ?? 0;

        onChange(displayAddress);

        if (onPlaceSelect) {
          onPlaceSelect({
            address: displayAddress,
            city,
            state,
            zipCode,
            country: "US",
            lat,
            lng,
          });
        } else {
          console.warn("❌ onPlaceSelect is not defined!");
        }
      } else {
        alert("Please select a US address only.");
      }

      // Restore scroll position to prevent unwanted scrolling
      requestAnimationFrame(() => {
        window.scrollTo(
          scrollPositionRef.current.x,
          scrollPositionRef.current.y,
        );
      });

      // Reset processing flag after a delay
      processingTimeout = setTimeout(() => {
        isProcessing = false;
        processingTimeout = null;
      }, 300);
    };

    const processPlace = () => {
      if (isProcessing) {
        return;
      }

      const place = autocompleteRef.current?.getPlace();

      if (!place || !place.address_components) {
        // Fallback: use geocoder with current input value
        const inputValue = inputRef.current?.value?.trim();
        if (inputValue && inputValue.length > 10) {
          // Reasonable address length
          tryGeocoderFallback(inputValue);
        }
        return;
      }

      // Use the shared processing function
      processPlaceData(place);
    };

    // Add the primary event listener for place selection
    const placeChangedListener = autocompleteRef.current.addListener(
      "place_changed",
      () => {
        // Process immediately without delay first
        processPlace();
      },
    );

    // Add DOM event listeners for keyboard navigation and fallback handling
    const inputElement = inputRef.current;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        // Only handle Enter key, let Tab work naturally
        setTimeout(() => processPlace(), 200);
      }
    };

    const handleBlur = () => {
      // Only trigger if the input has a complete address and we haven't processed it
      setTimeout(() => {
        const inputValue = inputRef.current?.value?.trim();
        if (inputValue && inputValue.length > 20 && !isProcessing) {
          processPlace();
        }
      }, 150);
    };

    // Add both keydown and blur listeners
    inputElement.addEventListener("keydown", handleKeyDown);
    inputElement.addEventListener("blur", handleBlur);

    return () => {
      if (placeChangedListener) {
        google.maps.event.removeListener(placeChangedListener);
      }

      // Clean up DOM event listeners
      if (inputElement) {
        inputElement.removeEventListener("keydown", handleKeyDown);
        inputElement.removeEventListener("blur", handleBlur);
      }

      // Clear any pending timeout
      if (processingTimeout) {
        clearTimeout(processingTimeout);
      }

      // Clean up mutation observer
      observer.disconnect();
    };
  }, [isGoogleLoaded, onChange, onPlaceSelect]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .pac-container {
            z-index: 10000 !important;
            pointer-events: auto !important;
          }
          .pac-item {
            pointer-events: auto !important;
            cursor: pointer !important;
          }
          .pac-item:hover {
            background-color: #f5f5f5 !important;
          }
        `,
        }}
      />
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={className}
        style={style}
        autoComplete="off"
      />
    </>
  );
};

export default CustomerAddressAutocomplete;
